import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CK_TOKEN_KEY, CK_USER_KEY, ckGetConfig, ckGetSite, ckGetMe,
  ckRegister, ckVerifyEmail, ckLoginStart, ckLoginVerify, ckResendCode,
} from './checkitApi';
import CheckitThemeStyle from '@/components/checkit/CheckitThemeStyle';
import { captureRef } from '@/lib/checkit/seoTrack';

const CheckitAuthContext = createContext(null);

export function useCheckit() {
  const ctx = useContext(CheckitAuthContext);
  if (!ctx) throw new Error('useCheckit must be used within CheckitAuthProvider');
  return ctx;
}

export function CheckitAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(CK_TOKEN_KEY) || null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CK_USER_KEY) || 'null'); } catch { return null; }
  });
  const [me, setMe] = useState(null);          // { plan, usage, allowed_tools, ... }
  const [config, setConfig] = useState(null);  // { plans, tool_min_tier, payments_configured }
  const [site, setSite] = useState(null);      // CMS content: hero, stats, testimonials, ...
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    captureRef();
    ckGetConfig().then((r) => setConfig(r.data)).catch(() => setConfig(null));
    ckGetSite().then((r) => {
      setSite(r.data);
      try {
        if (r.data?.theme) localStorage.setItem('checkit:theme', JSON.stringify(r.data.theme));
      } catch { /* ignore */ }
    }).catch(() => setSite(null));
  }, []);

  const refreshMe = useCallback(async () => {
    if (!localStorage.getItem(CK_TOKEN_KEY)) { setMe(null); return null; }
    try {
      const r = await ckGetMe();
      setMe(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 401) { logoutLocal(); }
      return null;
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => { await refreshMe(); setLoading(false); })();
  }, [refreshMe]);

  const applyLogin = useCallback(async (tok, usr) => {
    localStorage.setItem(CK_TOKEN_KEY, tok);
    if (usr) localStorage.setItem(CK_USER_KEY, JSON.stringify(usr));
    setToken(tok);
    setUser(usr || null);
    return await refreshMe();
  }, [refreshMe]);

  // ---- Two-step auth: email confirmation on signup + OTP on every login ----
  // registerStart / loginStart return a { challenge } to complete with a 6-digit
  // code; verifyEmail / verifyLoginOtp exchange that code for a session.
  const registerStart = useCallback(async (payload) => {
    const r = await ckRegister(payload);
    return r.data; // { requires_verification, challenge, email, email_sent, dev_code? }
  }, []);

  const loginStart = useCallback(async (email, password) => {
    const r = await ckLoginStart({ email, password });
    return r.data; // { requires_otp | requires_verification, challenge, email, dev_code? }
  }, []);

  const verifyEmail = useCallback(async (challenge, code) => {
    const r = await ckVerifyEmail(challenge, code);
    await applyLogin(r.data.token, r.data.user);
    return r.data;
  }, [applyLogin]);

  const verifyLoginOtp = useCallback(async (challenge, code) => {
    const r = await ckLoginVerify(challenge, code);
    await applyLogin(r.data.token, r.data.user);
    return r.data;
  }, [applyLogin]);

  const resendCode = useCallback(async (challenge) => {
    const r = await ckResendCode(challenge);
    return r.data; // { email_sent, dev_code? }
  }, []);

  function logoutLocal() {
    localStorage.removeItem(CK_TOKEN_KEY);
    localStorage.removeItem(CK_USER_KEY);
    setToken(null); setUser(null); setMe(null);
  }
  const logout = useCallback(() => { logoutLocal(); }, []);

  const value = {
    token, user, me, config, site, loading,
    isAuthed: !!token,
    plan: me?.plan || 'free',
    registerStart, loginStart, verifyEmail, verifyLoginOtp, resendCode,
    logout, applyLogin, refreshMe,
  };
  return (
    <CheckitAuthContext.Provider value={value}>
      <CheckitThemeStyle />
      {children}
    </CheckitAuthContext.Provider>
  );
}
